// Composer draft state (docs/06-ux-rules.md "Composer"; P6 decisions) —
// extracted out of `InboxComposer/index.tsx` per the repo convention ("when
// 3+ hooks accumulate in a scene, extract to a co-located useXxx.ts file";
// the component was carrying five `useState`s, four `useEffect`s, and a
// `useRef` directly). Owns the reward/points/subject/message draft, the
// per-item reset, the reward-only prefill (Q7), the dirty report to the
// container's discard guard, and the success/failure reset+`onSent` — the
// component itself is left with permission-derived render flags and JSX.
import type { FeedbackInboxDetail } from 'api/feedbackInbox.types'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { UseInboxActionsResult } from '../ThreadPane/useInboxActions'
import { isValidPoints, maxPointsFor } from './PointsInput'
import type { AvailableOffer } from './useAvailableOffers'

const t = buildTranslate('feedback_inbox.composer')

// Mirror nexus' resolve caps (api/v1/order_ratings.rb, api/v1/recovery/conversations.rb):
// MESSAGE_SUBJECT_MAX_LENGTH on `subject`, OPERATOR_NOTE_MAX_LENGTH on `reply_message`.
// Same values as SendMessageModal.
export const SUBJECT_MAX_LENGTH = 200
export const MESSAGE_MAX_LENGTH = 2000

export type RewardType = 'offer' | 'points'

export interface UseInboxComposerDraftOptions {
  detail: FeedbackInboxDetail
  actions: UseInboxActionsResult
  onSent?: () => void
  onDirtyChange?: (isDirty: boolean) => void
}

export interface UseInboxComposerDraftResult {
  rewardType: RewardType
  selectedOffer: AvailableOffer | null
  points: string
  subject: string
  message: string
  setSubject: (value: string) => void
  setMessage: (value: string) => void
  setPoints: (value: string) => void
  setSelectedOffer: (offer: AvailableOffer | null) => void
  handleRewardTypeChange: (next: RewardType) => void
  handleRemoveReward: () => void
  hasReward: boolean
  hasValidPoints: boolean
  // A non-empty Points entry that isn't a valid grant (over the kind's cap,
  // or not a positive integer) — Send must stay disabled while this is true,
  // since `points` alone going invalid doesn't clear the reward-only prefill
  // text still sitting in `message` if the operator has since edited it.
  pointsInvalid: boolean
  isDirty: boolean
  // Over the cap, measured on the trimmed value `send()` puts on the wire.
  subjectTooLong: boolean
  messageTooLong: boolean
  isOverLength: boolean
  // Builds the resolve body from the current draft and sends it. Callers
  // are expected to gate the click that reaches this (send-enabled rule,
  // permission, in-flight) themselves — this always sends unconditionally.
  send: () => void
}

export function useInboxComposerDraft({
  detail,
  actions,
  onSent,
  onDirtyChange,
}: UseInboxComposerDraftOptions): UseInboxComposerDraftResult {
  const [rewardType, setRewardType] = useState<RewardType>('offer')
  const [selectedOffer, setSelectedOffer] = useState<AvailableOffer | null>(
    null
  )
  const [points, setPoints] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  const buildDefaultSubject = useCallback(
    () =>
      detail.orderNumber
        ? t('subject_default', { orderNumber: detail.orderNumber })
        : t('subject_default_fallback'),
    [detail.orderNumber]
  )

  // Tracks the exact string the reward-only-prefill effect (below) last
  // wrote into `message`, so removing/switching the reward can retract ONLY
  // that text — never merchant-authored content the operator typed
  // themselves.
  const lastPrefillRef = useRef<string | null>(null)

  // Clears `message` if (and only if) it's still exactly the prefill text
  // this hook wrote — a merchant who has since edited it keeps their edit.
  const retractStalePrefill = useCallback(() => {
    setMessage(current => {
      if (
        lastPrefillRef.current !== null &&
        current === lastPrefillRef.current
      ) {
        lastPrefillRef.current = null
        return ''
      }
      return current
    })
  }, [])

  const resetDraft = useCallback(() => {
    setRewardType('offer')
    setSelectedOffer(null)
    setPoints('')
    setMessage('')
    setSubject(buildDefaultSubject())
    lastPrefillRef.current = null
  }, [buildDefaultSubject])

  // Fresh draft per item. Safe to reset unconditionally here — an unsent
  // draft on the OUTGOING item is the container discard-guard's job to
  // confirm BEFORE `detail.id` ever changes underneath this hook
  // (docs/06-ux-rules.md "Discard-confirm"; see `useDiscardGuard`).
  useEffect(() => {
    resetDraft()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id])

  const handleRewardTypeChange = useCallback(
    (next: RewardType) => {
      setRewardType(next)
      if (next === 'points') {
        setSelectedOffer(null)
      } else {
        setPoints('')
      }
      // The reward that produced the current prefill (if any) no longer
      // applies to the other path — retract it. If the newly-chosen path
      // already has a valid reward of its own, the effect below re-fills
      // with the correct label on the very next render.
      retractStalePrefill()
    },
    [retractStalePrefill]
  )

  const hasValidPoints =
    rewardType === 'points' && isValidPoints(points, maxPointsFor(detail.kind))
  const pointsInvalid =
    rewardType === 'points' && points.trim() !== '' && !hasValidPoints

  // Reward-only default body (Q7, closed) — a visible, editable prefill so
  // the merchant sees exactly what a reward-only reply says before sending
  // (docs/06-ux-rules.md "Composer" / "Reward-only default body"). Only
  // fills an EMPTY message — never overwrites something the merchant is
  // already typing.
  const offerLabel = selectedOffer
    ? selectedOffer.name
    : hasValidPoints
    ? t('points_amount', { count: Number(points) })
    : null

  useEffect(() => {
    if (!offerLabel) {
      // The reward that produced the current prefill, if any, no longer
      // validates (over cap, non-numeric, or cleared) — retract it so a
      // stale "Here's <N> points…" body can't sit there implying a grant
      // that `send()` will no longer include (PR #4173 review, LessThanInfinity).
      retractStalePrefill()
      return
    }
    // Blocked only by MERCHANT-authored text — an empty message, or a
    // message that's still exactly the PREVIOUS prefill (e.g. switching
    // from one offer to another), is fair game to (re)write.
    if (message.trim() !== '' && message !== lastPrefillRef.current) return
    const next = t('reward_only_body', { offer: offerLabel })
    lastPrefillRef.current = next
    setMessage(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerLabel])

  const hasReward = !!selectedOffer || hasValidPoints
  const isDirty =
    message.trim() !== '' || !!selectedOffer || points.trim() !== ''

  // Trimmed, like send() below: nexus' MaxLength measures the param as sent.
  // Codepoints, like nexus MaxLength (Ruby String#length), not UTF-16 units:
  // an emoji is 1 there but 2 in `.length` (es5 target: Array.from, not
  // `[...str]`).
  const charCount = (value: string): number => Array.from(value).length
  const subjectTooLong = charCount(subject.trim()) > SUBJECT_MAX_LENGTH
  const messageTooLong = charCount(message.trim()) > MESSAGE_MAX_LENGTH
  const isOverLength = subjectTooLong || messageTooLong

  // Read through a ref so the unmount-only effect below can't capture a
  // stale callback from the first render.
  const onDirtyChangeRef = useRef(onDirtyChange)
  onDirtyChangeRef.current = onDirtyChange

  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty)
  }, [isDirty])

  // Reported clean on UNMOUNT, in an effect of its own rather than as the
  // cleanup of the one above — that would also fire on every `isDirty`
  // toggle, reporting false then immediately true again. The composer
  // unmounts whenever `shouldShowComposer` goes false (the viewed item is
  // resolved, or a look-back lands on a resolved one); without this the
  // container's `hasUnsentDraft` stayed stuck `true` from the outgoing
  // item, raising a discard prompt for a composer that no longer exists.
  useEffect(() => () => onDirtyChangeRef.current?.(false), [])

  // Success/failure result (docs/06-ux-rules.md "Composer" / "Result"):
  // driven off the SHARED `actions.isPending` transitioning true → false,
  // not a manual await in the click handler — `actions` is a prop that
  // re-renders this hook's owner with fresh state once the mutation
  // settles, which sidesteps the stale-closure problem an awaited click
  // handler would have reading `actions.error` after the fact.
  //
  // `isPending`/`error` are shared across resolve AND unresolve (and, via
  // the same resolveState, the header's mark-as-resolved), so a settle of
  // EITHER looks identical here. `sendInFlightRef` scopes this hook's own
  // reaction to settles of a send THIS hook fired — otherwise a failed
  // Send's kept-alive draft gets silently wiped by an unrelated successful
  // Unresolve or header Resolve (PR #4168 review).
  const wasPendingRef = useRef(false)
  const sendInFlightRef = useRef(false)
  useEffect(() => {
    if (
      wasPendingRef.current &&
      !actions.isPending &&
      sendInFlightRef.current
    ) {
      sendInFlightRef.current = false
      if (!actions.error) {
        resetDraft()
        onSent?.()
      }
    }
    wasPendingRef.current = actions.isPending
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.isPending, actions.error])

  const handleRemoveReward = useCallback(() => {
    setSelectedOffer(null)
    setPoints('')
    retractStalePrefill()
  }, [retractStalePrefill])

  const send = useCallback(() => {
    sendInFlightRef.current = true
    actions.resolve({
      reply_message: message.trim() || undefined,
      subject: subject.trim() || undefined,
      reward_ids: selectedOffer ? [selectedOffer.id] : undefined,
      points: hasValidPoints ? Number(points) : undefined,
    })
  }, [actions, message, subject, selectedOffer, hasValidPoints, points])

  return {
    rewardType,
    selectedOffer,
    points,
    subject,
    message,
    setSubject,
    setMessage,
    setPoints,
    setSelectedOffer,
    handleRewardTypeChange,
    handleRemoveReward,
    hasReward,
    hasValidPoints,
    pointsInvalid,
    isDirty,
    subjectTooLong,
    messageTooLong,
    isOverLength,
    send,
  }
}
